import React from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Cookies from 'universal-cookie';
import { useDispatch } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import Logo from 'assets/img/Logo_EPS_300.png';
import 'assets/styles/login.css';

const cookies = new Cookies();

const Icon = ({ name }) => {
  const paths = {
    user: <><circle cx="12" cy="7" r="4"/><path d="M4.5 21v-2.4a5.6 5.6 0 0 1 5.6-5.6h3.8a5.6 5.6 0 0 1 5.6 5.6V21"/></>,
    shield: <><path d="M12 2.8 20 6v5.5c0 5.2-3.3 8.5-8 10.1-4.7-1.6-8-4.9-8-10.1V6l8-3.2Z"/><path d="m8.4 12 2.3 2.3 4.9-5"/></>,
    bolt: <path d="m13.2 2-8 11.1h6.2L10.8 22l8-11.1h-6.2L13.2 2Z"/>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="3"/><path d="m5 7 7 5 7-5"/></>,
    lock: <><rect x="4.5" y="10" width="15" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></>,
    eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></>,
    eyeOff: <><path d="m3 3 18 18"/><path d="M10.6 6.1A9.3 9.3 0 0 1 12 6c6 0 9.5 6 9.5 6a15 15 0 0 1-2.1 2.8M6.4 6.5C4 8.2 2.5 12 2.5 12s3.5 6 9.5 6c1.1 0 2.1-.2 3-.5"/></>,
    arrow: <><path d="M5 12h14"/><path d="m14 7 5 5-5 5"/></>
  };

  return (
    <svg className={`ae-icon ae-icon-${name}`} viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

const Benefit = ({ icon, tone, title, children }) => (
  <div className="ae-login-benefit">
    <div className={`ae-login-benefit-icon ae-login-benefit-icon--${tone}`}>
      <Icon name={icon} />
    </div>
    <div>
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  </div>
);

const MicrosoftMark = () => (
  <span className="ae-microsoft-mark" aria-hidden="true">
    <i /><i /><i /><i />
  </span>
);

export const LoginForm = ({
  loginSubmit,
  heading = 'Connexion',
  subtitle = 'Connectez-vous à votre compte',
  submitLabel = 'Se connecter',
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [remember, setRemember] = React.useState(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMounted = React.useRef(true);

  React.useEffect(() => () => {
    isMounted.current = false;
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email || !password || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await loginSubmit(email, password, remember, dispatch, history);
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <main className="ae-login-page">
      <section className="ae-login-intro" aria-label="Présentation Accès EPS">
        <div className="ae-login-ring ae-login-ring--green" />

        <div className="ae-login-intro-content">
          <img src={Logo} className="ae-login-logo" alt="Accès EPS" />

          <div className="ae-login-welcome">
            <h1>Bienvenue sur <span>Accès EPS</span></h1>
            <p>Votre espace dédié pour gérer vos accès<br className="ae-desktop-break" /> et données en toute simplicité.</p>
          </div>

          <div className="ae-login-benefits">
            <Benefit icon="shield" tone="green" title="Sécurisé">
              Vos données sont protégées<br /> et confidentielles.
            </Benefit>
            <Benefit icon="user" tone="blue" title="Personnalisé">
              Accédez à vos services<br /> selon votre profil.
            </Benefit>
            <Benefit icon="bolt" tone="gold" title="Simple & Rapide">
              Connectez-vous et retrouvez<br /> vos outils en un clic.
            </Benefit>
          </div>
        </div>

        <p className="ae-login-copyright">© 2024 Accès EPS — Tous droits réservés</p>
      </section>

      <section className="ae-login-main" aria-label="Connexion">
        <div className="ae-login-ring ae-login-ring--violet" />
        <div className="ae-login-ring ae-login-ring--gold" />

        <form className="ae-login-card" onSubmit={handleSubmit}>
          <div className="ae-login-card-body">
            <div className="ae-login-user-icon"><Icon name="user" /></div>
            <h2>{heading}</h2>
            <p className="ae-login-subtitle">{subtitle}</p>

            <div className="ae-login-fields">
              <label className="ae-login-field" htmlFor="login-email">
                <span>E-mail</span>
                <span className="ae-login-input-wrap">
                  <Icon name="mail" />
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="votre@email.com"
                    autoComplete="email"
                    required
                  />
                </span>
              </label>

              <label className="ae-login-field" htmlFor="login-password">
                <span>Mot de passe</span>
                <span className="ae-login-input-wrap">
                  <Icon name="lock" />
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Votre mot de passe"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    className="ae-login-password-toggle"
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    <Icon name={showPassword ? 'eyeOff' : 'eye'} />
                  </button>
                </span>
              </label>
            </div>

            <div className="ae-login-options">
              <label className="ae-login-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                <span>Se souvenir de moi</span>
              </label>
              <button
                className="ae-login-link"
                type="button"
                onClick={() => {
                  history.push('/change_mdp');
                }}
              >
                Mot de passe oublié ?
              </button>
            </div>

            <button className="ae-login-submit" type="submit" disabled={isSubmitting}>
              <span>{isSubmitting ? 'Connexion…' : submitLabel}</span>
              {!isSubmitting && <Icon name="arrow" />}
            </button>

            <div className="ae-login-divider"><span>ou</span></div>

            <button
              className="ae-login-microsoft"
              type="button"
              onClick={() => NotificationManager.info('La connexion Microsoft sera bientôt disponible.', '', 3000)}
            >
              <MicrosoftMark />
              <span>Se connecter avec Microsoft</span>
            </button>
          </div>

          <div className="ae-login-card-footer">
            <span>Vous n'avez pas de compte ?</span>
            <a href="mailto:contact@acces-editions.com">Contactez votre administrateur</a>
          </div>
        </form>
      </section>
    </main>
  );
};

const loginSubmit = (email, password, remember, dispatch, history) => {
  const formData = new FormData();
  formData.append('email', email);
  formData.append('password', password);

  return axios.post('https://eps.acces-editions.com/api/loginAppli', formData)
    .then((res) => {
      if (res.data.data[0] !== undefined && res.data.data[0].email) {
        if (res.data.session.authenticated === true) {
          cookies.remove('authenticated', { path: '/' });
          cookies.remove('authenticated_admin', { path: '/' });
          cookies.set('authenticated', 'true', remember ? { path: '/', maxAge: 3600 } : { path: '/' });
          cookies.set('statut', res.data.data[0].statut, { path: '/', maxAge: 3600 });
          dispatch({ type: 'user/userInfo', payload: { id_compte: res.data.data[0].id_compte, email: res.data.data[0].email, admin: false } });
          history.replace('/compte');
        }
      } else {
        NotificationManager.error('E-mail ou mot de passe invalide', '', 3000);
      }
    })
    .catch(() => {
      NotificationManager.error('La connexion au service a échoué. Veuillez réessayer.', '', 3000);
    });
};

const LoginClass = () => <LoginForm loginSubmit={loginSubmit} />;

export default LoginClass;
